import React, { useCallback, useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { apiServices } from '../utils/apiServices';
import { IDepositorResponse } from '../utils/types';
import { useAppContext } from '../context/AppContext';
import { toast } from 'react-toastify';
import { formatDate, parseContractEvents } from '../utils';
import DepositorDetailOverview from '../components/deposit/DepositorDetailOverview';
import { sendTransaction } from '../utils/polkadotUtils';
import { DepositsHistoryTable } from '../components/deposit/DepositsHistoryTable';
import { ArrowDownIcon, ArrowUpIcon, ChevronLeftIcon } from '../components/SVGIcons';
import { useTheme } from '../context/ThemeContext';
import DepositorDepositWithdrawModal from '../components/deposit/DepositorDepositWithdrawModal';
import { useEVMContext } from '../context/EVMContext';
import { ethers } from 'ethers';

const DepositorDetailPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { signer, provider, accounts, poolContract } = useEVMContext();
  const [depositorData, setDepositorData] = useState<IDepositorResponse | null>(null);
  const [error, setError] = useState<string | null>(null);
  const { api, setIsLoading } = useAppContext();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [actionType, setActionType] = useState<'deposit' | 'withdraw'>('deposit');
  const [amount, setAmount] = useState('');
  const { theme } = useTheme();

  const [balance, setBalance] = useState<string>('');

  const updateBalance = useCallback(async () => {
    if (!(window as any).ethereum) {
      throw new Error('No EVM wallet detected');
    }
    const connectedWalletAddress = await signer?.getAddress();
    if (connectedWalletAddress && provider && id == connectedWalletAddress) {
      const balance = await provider.getBalance(connectedWalletAddress);
      console.log('EVM Balance:', ethers.formatEther(balance));
      // Convert balance from wei to ether
      setBalance(ethers.formatEther(balance));
    } else {
      setBalance('');
    }
  }, [signer, id, provider]);

  useEffect(() => {
    updateBalance();
  }, [updateBalance]);

  const handleDeposit = async () => {
    setActionType('deposit');
    setIsModalOpen(true);
  };

  const handleWithdraw = async () => {
    setActionType('withdraw');
    setIsModalOpen(true);
  };

  const handleSubmit = async () => {
    if (!id) {
      toast.error('Depositor ID is not defined');
      return;
    }
    if (!poolContract || !signer) {
      toast.error('EVM Wallet is not connected');
      return;
    }
    setIsLoading(true);
    const connectedWalletAddress = await signer?.getAddress();

    if (actionType === 'deposit') {
      try {
        poolContract.addLiquidity({
          value: ethers.parseEther(amount)
        }).then((tx: any) => {
          return tx.wait();
        }).then(async (receipt: any) => {
          // Parse event from logs
          parseContractEvents(receipt, poolContract);
          toast.success('Deposit successful!');

          const res = await apiServices.pool.refresh(connectedWalletAddress, false);
          setDepositorData(res as IDepositorResponse);
          updateBalance();
        }).catch((error: any) => {
          console.error('Error while deposits:', error);
          toast.error(`Failed to deposit: ${error.message || 'Please try again.'}`);
        });
      } catch (error) {
        console.log(`Failed to ${actionType}:`, error);
        toast.error(`Failed to ${actionType}. Please try again.`);
      }
    } else {
      try {
        poolContract.removeLiquidity(ethers.parseEther(amount)).then((tx: any) => {
          return tx.wait();
        }).then((tx: any) => {
          return tx.wait();
        }).then(async (receipt: any) => {
          // Parse event from logs
          parseContractEvents(receipt, poolContract);
          toast.success('Withdrawal successful!');

          const res = await apiServices.pool.refresh(connectedWalletAddress, false);
          setDepositorData(res as IDepositorResponse);
          updateBalance();
        }).catch((error: any) => {
          console.error('Error while withdrawing:', error);
          toast.error(`Failed to withdraw: ${error.message || 'Please try again.'}`);
        });
      } catch (error) {
        console.log(`Failed to ${actionType}:`, error);
        toast.error(`Failed to ${actionType}. Please try again.`);
        setIsLoading(false);
      }
    }
    setIsLoading(false);
    setIsModalOpen(false);
    setAmount('');
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setAmount('');
  };

  useEffect(() => {
    const fetchDepositorData = async () => {
      if (!id) return;
      setIsLoading(true);
      setError(null);
      try {
        const res = await apiServices.depositors.getById(id);
        setDepositorData(res);
      } catch (err) {
        console.error('Failed to fetch depositor details:', err);
        setError(err instanceof Error ? err.message : 'Failed to fetch depositor details');
        setDepositorData(null);
      } finally {
        setIsLoading(false);
      }
    };

    fetchDepositorData();
  }, [id]);

  const depositorsListData = {
    id: id,
    address: id,
    totalDeposits: Number(depositorData?.locked_amount || 0) + Number(depositorData?.available_amount || 0),
    depositPercentage: Number(depositorData?.deposit_percent),
    joinDate: formatDate(depositorData?.created_at),
    availableAmount: Number(depositorData?.available_amount),
    lockedAmount: Number(depositorData?.locked_amount),
    lastDeposit: depositorData?.updated_at,
    depositHistory: depositorData?.deposits
  };

  return (
    <div className="min-h-screen p-4 sm:p-6 lg:p-8">
      <div className="max-w-7xl mx-auto">
        <div className="mb-4 sm:mb-6 flex flex-col sm:flex-row sm:items-center justify-between space-y-4 sm:space-y-0">
          <div className="flex items-center">
            <button
              onClick={() => navigate(-1)}
              className="rounded-[100px] p-2 bg-gray-200 dark:bg-gray-800 mr-4"
            >
              <ChevronLeftIcon fillColor={theme === 'dark' ? 'white' : 'black'} />
            </button>
            <h1 className="text-2xl sm:text-3xl text-black dark:text-white font-borna">Depositor Details</h1>
          </div>
          {error && (
            <div className="text-red-600 text-sm">
              {error}
            </div>
          )}
          {(accounts[0] == id) && (
            <div className="flex flex-col sm:flex-row gap-2 sm:gap-4">
              <button
                onClick={handleDeposit}
                className="px-4 py-2 bg-green-500 text-white dark:text-black rounded-[100px] hover:bg-green-700 transition-colors text-[14px] h-[40px] sm:h-[46px] px-[16px] sm:px-[24px] flex items-center justify-center"
              >
                <ArrowDownIcon fillColor={theme === 'dark' ? 'black' : 'white'} />
                <span className="ml-2">Deposit</span>
              </button>
              <button
                onClick={handleWithdraw}
                className="px-4 py-2 bg-gray-100 hover:bg-gray-300 dark:bg-gray-800 text-black dark:text-white rounded-[100px] dark:hover:bg-gray-500 transition-colors text-[14px] h-[40px] sm:h-[46px] px-[16px] sm:px-[24px] flex items-center justify-center"
              >
                <ArrowUpIcon fillColor={theme === 'dark' ? 'white' : 'black'} />
                <span className="ml-2">Withdraw</span>
              </button>
            </div>
          )}
        </div>

        <div className="grid grid-cols-1 gap-4 sm:gap-6">
          {/* Overview Card */}
          <DepositorDetailOverview id={id || ''} depositorsListData={depositorsListData} balance={balance} />

          {/* Deposit History */}
          <DepositsHistoryTable depositHistory={depositorsListData?.depositHistory || []} />
        </div>
      </div>

      {/* Modal */}
      {isModalOpen && <DepositorDepositWithdrawModal actionType={actionType} handleCloseModal={handleCloseModal} handleSubmit={handleSubmit} amount={amount} setAmount={setAmount} />}
    </div>
  );
};

export default DepositorDetailPage; 